import * as THREE from 'three';
import { GAME, type CarryKind } from '../config';
import { createCarryItemMesh } from '../meshes/factory';

export class StackCarrier {
  readonly group = new THREE.Group();
  private readonly items: { kind: CarryKind; mesh: THREE.Group }[] = [];
  private readonly bob = new THREE.Vector3();
  private max: number;

  constructor(max: number = GAME.baseStackMax) {
    this.max = max;
    this.group.name = 'stack';
  }

  setMax(n: number): void {
    this.max = Math.max(1, n);
  }

  get capacity(): number {
    return this.max;
  }

  get count(): number {
    return this.items.length;
  }

  get kinds(): CarryKind[] {
    return this.items.map((i) => i.kind);
  }

  counts(): Record<CarryKind, number> {
    const c = emptyCounts();
    for (const item of this.items) c[item.kind] += 1;
    return c;
  }

  canAdd(n = 1): boolean {
    return this.items.length + n <= this.max;
  }

  add(kind: CarryKind): boolean {
    if (!this.canAdd()) return false;
    const mesh = createCarryItemMesh(kind);
    this.group.add(mesh);
    this.items.push({ kind, mesh });
    this.layout();
    return true;
  }

  take(kind: CarryKind, amount: number): number {
    let taken = 0;
    for (let i = this.items.length - 1; i >= 0 && taken < amount; i--) {
      if (this.items[i]!.kind !== kind) continue;
      const [removed] = this.items.splice(i, 1);
      if (!removed) break;
      this.group.remove(removed.mesh);
      disposeObject(removed.mesh);
      taken += 1;
    }
    if (taken) this.layout();
    return taken;
  }

  takeAnyFlowers(amount: number): { kind: CarryKind; count: number }[] {
    const out: { kind: CarryKind; count: number }[] = [];
    let left = amount;
    const flowers: CarryKind[] = [
      'rose',
      'bluebell',
      'fern',
      'sunflower',
      'lavender',
      'tulip',
      'orchid',
      'crystal',
    ];
    for (const kind of flowers) {
      if (left <= 0) break;
      const n = this.take(kind, left);
      if (n > 0) {
        out.push({ kind, count: n });
        left -= n;
      }
    }
    return out;
  }

  takeTop(amount: number): CarryKind[] {
    const out: CarryKind[] = [];
    while (out.length < amount && this.items.length > 0) {
      const removed = this.items.pop()!;
      this.group.remove(removed.mesh);
      disposeObject(removed.mesh);
      out.push(removed.kind);
    }
    this.layout();
    return out;
  }

  clear(): void {
    while (this.items.length) {
      const removed = this.items.pop()!;
      this.group.remove(removed.mesh);
      disposeObject(removed.mesh);
    }
  }

  update(dt: number, moving: boolean, facing: number): void {
    this.group.position.x = THREE.MathUtils.damp(this.group.position.x, Math.sin(facing) * -0.18, 8, dt);
    this.group.position.z = THREE.MathUtils.damp(this.group.position.z, Math.cos(facing) * -0.28, 8, dt);
    this.bob.y += dt * (moving ? 10 : 4);
    this.layout();
  }

  private layout(): void {
    const h = GAME.stackItemHeight;
    for (let i = 0; i < this.items.length; i++) {
      const mesh = this.items[i]!.mesh;
      const sway = Math.sin(this.bob.y + i * 0.35) * 0.02 * (1 + i * 0.015);
      mesh.position.set(sway, 0.95 + i * h, sway * 0.5);
      mesh.rotation.y = i * 0.12;
      mesh.scale.setScalar(1 - Math.min(i, 24) * 0.006);
    }
  }

  dispose(): void {
    this.clear();
  }
}

export function emptyCounts(): Record<CarryKind, number> {
  return {
    rose: 0,
    bluebell: 0,
    fern: 0,
    sunflower: 0,
    lavender: 0,
    tulip: 0,
    orchid: 0,
    crystal: 0,
    honey: 0,
    cash: 0,
  };
}

function disposeObject(obj: THREE.Object3D): void {
  obj.traverse((child) => {
    if (child instanceof THREE.Mesh) child.geometry?.dispose();
  });
}
